import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
// @material-ui/icons
import HideIco from '@material-ui/icons/VisibilityOff';
// @material-ui/core components
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import Hidden from '@material-ui/core/Hidden';
import FormControl from '@material-ui/core/FormControl';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import GridContainer from 'components/UI/Grid/GridContainer';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import Select from '@material-ui/core/Select/Select';
import WithPermission from 'components/User/WithPermission';

/**
 * @description
 * Stateless component to handle user filters
 *
 * @param {Object} props - Component props.
 */
function UserAgencyFilter(props) {
  const { classes, filter, appliedFilter, agencies, showFilters, t } = props;

  return (
    <GridContainer>
      <GridItem xs={12} sm={4} md={4}>
        <SectionDivider label={t('user.filter.users')}>
          <div className={classes.responsiveHorizontalScroll}>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== undefined}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: undefined,
                });
              }}
              id="filterUserAll"
              key="filterUserAll"
              data-cy="filterUserAll"
            >
              {t('user.filter.all')}
            </Button>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== true}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: true,
                });
              }}
              id="filterUserActive"
              data-cy="filterActiveUser"
            >
              {t('user.filter.active')}
            </Button>
            <Button
              color="info"
              round
              simple={appliedFilter.isActive !== false}
              onClick={() => {
                filter({
                  ...appliedFilter,
                  isActive: false,
                });
              }}
              id="filterUserInactive"
              key="filterUserInactive"
              data-cy="filterInactiveUser"
            >
              {t('user.filter.inactive')}
            </Button>
          </div>
        </SectionDivider>
      </GridItem>
      <WithPermission allowPermissions={['list_user_agency']}>
        <WithPermission allowPermissions={['list_agency']}>
          <GridItem xs={12} sm={4} md={3}>
            <SectionDivider label={t('userAgency.agency')}>
              <FormControl fullWidth className={classes.filterSelect}>
                <Select
                  id="agency"
                  MenuProps={{
                    className: classes.selectMenu,
                  }}
                  classes={{
                    select: classes.select,
                  }}
                  name="agency"
                  onChange={event => {
                    filter({
                      ...appliedFilter,
                      agency: event.target.value,
                    });
                  }}
                  value={appliedFilter.agency || ''}
                  className={classes.reviewDD}
                  displayEmpty
                >
                  <MenuItem
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    value=""
                  >
                    {t('common.all')}
                  </MenuItem>
                  {agencies.map(item => (
                    <MenuItem
                      key={item.node.id}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                      value={item.node.id}
                    >
                      {item.node.fullname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SectionDivider>
          </GridItem>
        </WithPermission>
      </WithPermission>

      <GridItem xs={12}>
        <GridContainer justify="space-between">
          <Button
            color="primary"
            simple
            className={classes.buttons}
            onClick={showFilters}
            id="hideFilters"
            key="hideFilters"
          >
            <HideIco />
            {t('user.filter.hide')}
            <Hidden xsDown implementation="css">
              <div style={{ marginLeft: 4 }}>Filtros</div>
            </Hidden>
          </Button>
          <Button
            color="danger"
            disabled={
              appliedFilter.agency === undefined &&
              appliedFilter.isActive === undefined
            }
            simple
            onClick={() => {
              const cleanData = { isActive: undefined, agency: undefined };
              filter(cleanData);
            }}
            id="resetFilters"
            key="resetFilters"
          >
            {t('user.filter.clean')}
            <Hidden xsDown implementation="css">
              <div style={{ marginLeft: 4 }}>
                {t('user.filter.clean_break')}
              </div>
            </Hidden>
          </Button>
        </GridContainer>
      </GridItem>
    </GridContainer>
  );
}

/**
 * Filter PropTypes
 * @param classes - prop styles received from withStyles
 * @param appliedFilter - prop to know applied filters
 * @param allowRanks - prop allowed ranks
 * @param permissionsGroups - prop permissions groups received from container
 * @param t - prop from HOC with string translations
 * @callback filter - callback called when user clicks filter button
 * @callback showFilters - callback called when user clicks collapse filters
 * button
 */

UserAgencyFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  showFilters: PropTypes.func.isRequired,
  agencies: PropTypes.instanceOf(Array),
  t: PropTypes.func.isRequired,
};

UserAgencyFilter.defaultProps = {
  agencies: [],
};

export default withTranslation()(
  withStyles(extendedFormsStyle)(UserAgencyFilter),
);
